import { useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { FiltersDefinition, GroupingDefinition } from '@factorialco/f0-react'
import {
  DataCollectionSourceDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  OnBulkActionCallback,
  SortingsDefinition,
  SummariesDefinition,
} from '@factorialco/f0-react/dist/experimental'
import { Cross as CrossIcon } from '@factorialco/f0-react/icons/app'
import {
  BulkApproveApprovalsv2ReviewRequestDocument,
  BulkRejectApprovalsv2ReviewRequestDocument,
  InboxTaskListItemCompletionFragment,
  InboxTaskListItemFragment,
  TasksValueObjectsTaskKindEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'
import uniqBy from 'lodash/uniqBy'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useBulkApproveRejectFlows from 'modules/approvals/hooks/useBulkApproveRejectFlows'
import { getTaskActions } from 'modules/inbox_tasks/lib/actions'
import { trackClickInboxAction } from 'modules/inbox_tasks/lib/events'
import { getTaskUrl } from 'modules/inbox_tasks/lib/helpers'
import {
  readTaskFromCache,
  useMarkTaskAsDoneInCache,
  writeTaskToCache,
} from 'modules/inbox_tasks/lib/markTaskAsDoneInCache'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

type GraphqlDataSourceDefinition = DataCollectionSourceDefinition<
  InboxTaskListItemFragment & InboxTaskListItemCompletionFragment,
  FiltersDefinition,
  SortingsDefinition,
  SummariesDefinition,
  ItemActionsDefinition<InboxTaskListItemFragment & InboxTaskListItemCompletionFragment>,
  NavigationFiltersDefinition,
  GroupingDefinition<InboxTaskListItemFragment & InboxTaskListItemCompletionFragment>
>

export const useActions = () => {
  const apolloClient = useApolloClient()

  const { access, company, employee } = useCurrentSession()

  const { navigation } = useNavigation()

  const { search } = useLocation()

  const navigate = useNavigate()

  const currentAccessId = access.id

  const { approve, reject } = useBulkApproveRejectFlows()

  const updateTaskStatuses = useUpdateTaskStatuses()

  const markTaskAsDoneInCache = useMarkTaskAsDoneInCache()

  const [bulkApproveV2] = useMutation(BulkApproveApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkApproveApprovalsv2ReviewRequest,
    optimisticResponse: {
      approvalsV2Mutations: {
        __typename: 'approvalsV2Mutations',
        bulkApproveApprovalsv2ReviewRequest: {
          __typename: 'BulkApproveApprovalsv2ReviewRequestPayload',
          errors: [],
        },
      },
    },
    onError: (_, options) => {
      const reviewRequestIds: string[] = options?.variables?.reviewRequestIds ?? []
      const taskIds = reviewRequestIds
        .map((reviewRequestId) => reviewRequestIdsToTaskIds.current[reviewRequestId])
        .filter(Boolean)
      undoSuccessApproveReject(taskIds)
    },
  })

  const [bulkRejectV2] = useMutation(BulkRejectApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkRejectApprovalsv2ReviewRequest,
    optimisticResponse: {
      approvalsV2Mutations: {
        __typename: 'approvalsV2Mutations',
        bulkRejectApprovalsv2ReviewRequest: {
          __typename: 'BulkRejectApprovalsv2ReviewRequestPayload',
          errors: [],
        },
      },
    },
    onError: (_, options) => {
      const reviewRequestIds: string[] = options?.variables?.reviewRequestIds ?? []
      const taskIds = reviewRequestIds
        .map((reviewRequestId) => reviewRequestIdsToTaskIds.current[reviewRequestId])
        .filter(Boolean)
      undoSuccessApproveReject(taskIds)
    },
  })

  const reviewRequestIdsToTaskIds = useRef<Record<string, string>>({})

  const originalTaskData = useRef<Record<string, InboxTaskListItemCompletionFragment>>({})

  const getCachedTaskData = useCallback(
    (taskId: string) => readTaskFromCache(apolloClient, taskId),
    [apolloClient]
  )

  const handleSuccessApproveReject = useCallback(
    (taskIds: string[]) => {
      taskIds.forEach((taskId) => {
        const cachedTaskData = readTaskFromCache(apolloClient, taskId)

        if (cachedTaskData) {
          originalTaskData.current[taskId] = cachedTaskData
        }

        markTaskAsDoneInCache(taskId)
      })
    },
    [markTaskAsDoneInCache]
  )

  const undoSuccessApproveReject = useCallback(
    (taskIds: string[]) => {
      taskIds.forEach((taskId) => {
        const previousCachedTaskData = originalTaskData.current[taskId]

        if (!previousCachedTaskData) return

        writeTaskToCache(apolloClient, taskId, previousCachedTaskData)
      })
    },
    [apolloClient]
  )

  const getTrackingParams = useCallback(
    (
      action: string,
      item: Pick<
        InboxTaskListItemFragment,
        'taskable' | 'id' | 'resourceId' | 'groupedTasksConnection'
      >
    ) => {
      let approvableTypename: string | undefined
      let approvalSuggestion: string | undefined

      if (item.taskable) {
        // For approval tasks, get typename from the approvable, not the taskable
        if (
          item.taskable.__typename === 'ApprovalsMaterializedApprovalStep' &&
          'materializedApprovalFlow' in item.taskable
        ) {
          const approvable = item.taskable.materializedApprovalFlow?.approvable
          approvableTypename = approvable?.__typename

          // Get approval suggestion for TimeoffLeave
          if (approvable?.__typename === 'TimeoffLeave' && 'approvalSuggestion' in approvable) {
            approvalSuggestion = approvable.approvalSuggestion ?? undefined
          }
        } else {
          // For non-approval tasks, use the taskable typename
          approvableTypename = item.taskable.__typename
        }
      }

      return {
        type: action,
        approvable_typename: approvableTypename,
        approval_suggestion: approvalSuggestion,
      }
    },
    []
  )

  const handleTakeBulkAction = useCallback(
    async (
      action: string,
      originalCheckedItems: (Pick<
        InboxTaskListItemFragment,
        'taskable' | 'id' | 'resourceId' | 'groupedTasksConnection'
      > &
        Partial<Pick<InboxTaskListItemCompletionFragment, 'status'>>)[]
    ) => {
      //remove grouped expensabes
      const checkedItems = originalCheckedItems.filter((item) => {
        const taskable = item.taskable
        if (taskable?.__typename !== 'ApprovalsMaterializedApprovalStep') return true

        const approvable = taskable.materializedApprovalFlow?.approvable
        if (approvable?.__typename !== 'ExpensesExpensable') return true

        return !approvable.expensableGroup
      })

      const firstCheckedItem = checkedItems?.[0]

      const oldApprovalItems = checkedItems.filter(
        (item) => item.taskable && 'materializedApprovalFlow' in item.taskable
      )

      const newApprovalItems = checkedItems.filter(
        (item) => item.taskable && 'approvalProcess' in item.taskable
      )

      const checkedMaterializedFlowIds = oldApprovalItems
        .map((item) => {
          if (item.taskable && 'materializedApprovalFlow' in item.taskable) {
            return item.taskable?.materializedApprovalFlow?.id
          }
        })
        .filter(Boolean)

      const newApprovalReviewRequestIds = newApprovalItems
        .map((item) => {
          if (item.taskable && 'approvalProcess' in item.taskable) {
            if (item.taskable?.id) {
              reviewRequestIdsToTaskIds.current[item.taskable?.id] = item.id
            }
            return item.taskable?.id
          }
        })
        .filter(Boolean)

      const approveRejectParams = {
        accessId: currentAccessId,
        materializedFlowIds: checkedMaterializedFlowIds,
      }

      const checkedItemIds = checkedItems.map((item) => item.id)

      checkedItemIds.forEach((taskId) => {
        const cachedTaskData = getCachedTaskData(taskId)

        if (cachedTaskData) {
          originalTaskData.current[taskId] = cachedTaskData
        }
      })

      const manualTaskActions: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-in-progress': 'in_progress',
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-discarded': 'discarded',
      }

      const manualTaskActionStatus = manualTaskActions[action]

      await new Promise((resolve) => setTimeout(resolve, 25))

      if (action === 'approve') {
        // Track approve events only for single item actions, not bulk actions
        if (checkedItems.length === 1 && firstCheckedItem) {
          trackClickInboxAction(getTrackingParams('approve', firstCheckedItem))
        }

        // Handle old approvals
        if (oldApprovalItems.length > 0) {
          approve(
            approveRejectParams,
            () => {},
            () => {
              undoSuccessApproveReject(oldApprovalItems.map((item) => item.id))
            },
            false
          )
          app.setFlash({
            type: 'correct',
            message: i18n.t('settings.approvals.approval_action.flash_messages.approve'),
            time: 2000,
          })
          handleSuccessApproveReject(oldApprovalItems.map((item) => item.id))
        }

        // Handle new approvals
        if (newApprovalItems.length > 0) {
          const result = await bulkApproveV2({
            variables: {
              companyId: company.id,
              reviewRequestIds: newApprovalReviewRequestIds,
              reviewerId: employee.id,
            },
          })

          const handled = handleResult(result, () => {})

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('settings.approvals.approval_action.flash_messages.approve'),
            })
            handleSuccessApproveReject(newApprovalItems.map((item) => item.id))
          }
        }
      } else if (action === 'reject') {
        // Track reject events only for single item actions, not bulk actions
        if (checkedItems.length === 1 && firstCheckedItem) {
          trackClickInboxAction(getTrackingParams('reject', firstCheckedItem))
        }

        // Handle old approvals if only one item is selected
        if (oldApprovalItems.length === 1 && firstCheckedItem) {
          const taskId = firstCheckedItem.id
          const materializedFlowId =
            firstCheckedItem.taskable && 'materializedApprovalFlow' in firstCheckedItem.taskable
              ? firstCheckedItem.taskable?.materializedApprovalFlow?.id
              : undefined
          navigate({
            search,
            hash: encodeActivity('core.rejectApprovalFlow', {
              taskId: `${taskId}`,
              materializedFlowId: `${materializedFlowId}`,
            }),
          })
        } else if (oldApprovalItems.length > 0) {
          reject(
            approveRejectParams,
            () => {},
            () => {
              undoSuccessApproveReject(oldApprovalItems.map((item) => item.id))
            },
            false
          )
          app.setFlash({
            type: 'correct',
            message: i18n.t('settings.approvals.approval_action.flash_messages.reject'),
            time: 2000,
          })
          handleSuccessApproveReject(oldApprovalItems.map((item) => item.id))
        }

        // Handle new approvals
        if (newApprovalItems.length > 0) {
          const result = await bulkRejectV2({
            variables: {
              companyId: company.id,
              reviewRequestIds: newApprovalReviewRequestIds,
              reviewerId: employee.id,
              reason: undefined, // No reason for bulk reject from inbox
            },
          })

          const handled = handleResult(result, () => {})

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('settings.approvals.approval_action.flash_messages.reject'),
            })
            handleSuccessApproveReject(newApprovalItems.map((item) => item.id))
          }
        }
      } else if (manualTaskActionStatus) {
        updateTaskStatuses({ taskIds: checkedItemIds, status: manualTaskActionStatus })
      } else if (firstCheckedItem) {
        const url = getTaskUrl({
          taskableInfoForUrl: firstCheckedItem.taskable,
          resourceId: firstCheckedItem?.resourceId ?? undefined,
          navigation,
          groupedCount: firstCheckedItem.groupedTasksConnection?.totalCount ?? 0,
          taskId: firstCheckedItem.id,
          status: firstCheckedItem.status ?? undefined,
        })

        const urlToOpen =
          url ??
          `${location.pathname}#${encodeActivity('core.task', { id: `${firstCheckedItem.id}` })}`

        navigate(urlToOpen)
      }
    },
    [
      approve,
      reject,
      bulkApproveV2,
      bulkRejectV2,
      company.id,
      employee.id,
      currentAccessId,
      handleSuccessApproveReject,
      undoSuccessApproveReject,
      navigation,
      navigate,
      updateTaskStatuses,
      getCachedTaskData,
      search,
      getTrackingParams,
    ]
  )

  const handleBulkAction = useCallback<
    OnBulkActionCallback<InboxTaskListItemFragment, FiltersDefinition>
  >(
    async (action, selectedItems) => {
      const checkedItems = selectedItems.itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)
      return handleTakeBulkAction(action, checkedItems)
    },
    [handleTakeBulkAction]
  )

  const getTaskListItemActions = useCallback<
    NonNullable<GraphqlDataSourceDefinition['itemActions']>
  >(
    (item) => {
      if (
        item.taskable?.__typename === 'FormsMyMembership' &&
        item.status !== 'discarded' &&
        item.status !== 'done'
      ) {
        return [
          {
            label: i18n.t('tasks.mark.as', {
              status: i18n.t('tasks.status.discarded').toLowerCase(),
            }),
            icon: CrossIcon,
            critical: true,
            onClick: () => handleTakeBulkAction('mark-as-discarded', [item]),
            type: 'primary' as const,
          },
        ]
      }

      const whitelistedTaskKinds: TasksValueObjectsTaskKindEnum[] = ['approve', 'terminate']

      if (!item.kind || (item.kind && !whitelistedTaskKinds.includes(item.kind))) return

      if (
        item.taskable?.__typename === 'ApprovalsMaterializedApprovalStep' &&
        item.taskable.materializedApprovalFlow?.approvable?.__typename === 'ExpensesExpensable'
      ) {
        if (item.taskable.materializedApprovalFlow?.approvable?.expensableGroup) return
      }

      const actions = getTaskActions({
        taskKind: item.kind,
        status: item?.status ?? undefined,
      })

      return [
        actions.primaryAction && {
          label: actions.primaryAction.label,
          icon: actions.primaryAction.icon,
          onClick: () =>
            actions.primaryAction ? handleTakeBulkAction(actions.primaryAction.id, [item]) : null,
          type: 'primary' as const,
        },
        actions.secondaryAction && {
          label: actions.secondaryAction.label,
          icon: actions.secondaryAction.icon,
          onClick: () =>
            actions.secondaryAction
              ? handleTakeBulkAction(actions.secondaryAction.id, [item])
              : null,
          type: 'primary' as const,
        },
      ].filter(Boolean)
    },
    [handleTakeBulkAction]
  )

  const bulkActions = useCallback<NonNullable<GraphqlDataSourceDefinition['bulkActions']>>(
    ({ itemsStatus }) => {
      const itemsSelected = itemsStatus.filter((item) => item.checked)

      const allManualSelected = itemsSelected.every(
        ({ item }) => !item.kind || item.kind === 'manual'
      )

      if (allManualSelected) {
        const actions = uniqBy(
          itemsSelected
            .map(
              ({ item }) =>
                getTaskActions({
                  taskKind: 'manual',
                  status: item.status ?? undefined,
                })?.dropdownActions
            )
            .flat(),
          (action) => (action && 'value' in action ? action.value : undefined)
        ).filter(Boolean)

        return {
          primary: actions
            .map((action) =>
              'label' in action
                ? {
                    id: action.value,
                    label: action.label,
                  }
                : null
            )
            .filter(Boolean),
        }
      }

      const someCompletedItemsSelected = itemsSelected.some(({ item }) => !!item.completedAt)

      if (someCompletedItemsSelected) {
        return {
          warningMessage: i18n.t('tasks.no_action_for_completed_tasks'),
        }
      }

      const numberOfItemsSelected = itemsSelected.length

      const firstSelectedItem = itemsSelected?.[0]?.item

      const firstSelectedTaskKind = firstSelectedItem?.kind
      const firstSelectedTaskStatus = firstSelectedItem?.status ?? undefined

      if (numberOfItemsSelected === 1 && firstSelectedTaskKind) {
        const actions = getTaskActions({
          taskKind: firstSelectedTaskKind,
          status: firstSelectedTaskStatus,
        })

        if (actions.dropdownActions) {
          return {
            primary: actions.dropdownActions
              .map((action) =>
                'value' in action
                  ? {
                      id: action.value,
                      label: action.label,
                    }
                  : null
              )
              .filter(Boolean),
          }
        }

        if (actions.primaryAction && !actions.secondaryAction) {
          return {
            primary: [],
            secondary: [actions.primaryAction],
          }
        }

        return {
          primary: actions.primaryAction ? [actions.primaryAction] : [],
          secondary: actions.secondaryAction ? [actions.secondaryAction] : [],
        }
      }

      const approvableSelected = itemsSelected.some(
        ({ item }) =>
          (item.taskable &&
            ('materializedApprovalFlow' in item.taskable || 'approvalProcess' in item.taskable) &&
            !item.completedAt) ||
          !item.kind ||
          item.kind === 'manual'
      )

      if (!approvableSelected) {
        return {
          warningMessage: i18n.t('tasks.cannot_take_bulk_action'),
        }
      }

      const allApprovable = itemsSelected.every(({ item }) => {
        if (item.taskable && 'materializedApprovalFlow' in item.taskable) {
          return !!item.taskable?.materializedApprovalFlow && !item.completedAt
        }
        if (item.taskable && 'approvalProcess' in item.taskable) {
          return !!item.taskable?.approvalProcess && !item.completedAt
        }
        return false
      })

      if (!allApprovable) {
        return {
          warningMessage: i18n.t('tasks.different_actions'),
        }
      }

      const approvableActions = getTaskActions({ taskKind: 'approve' })

      if (!approvableActions.primaryAction || !approvableActions.secondaryAction) {
        return {
          primary: [],
          secondary: [],
        }
      }

      return {
        primary: [approvableActions.primaryAction],
        secondary: [approvableActions.secondaryAction],
      }
    },
    []
  )

  return {
    bulkActions,
    getCachedTaskData,
    handleBulkAction,
    getTaskListItemActions,
  }
}
